/*
 * ts-autocomplete.js — Gemeinsames Typesense-Autocomplete-Modul (kein Build-Step).
 *
 * Eine Implementierung fuer alle Sofortsuche-Eingaben (Desktop-Header,
 * Vollbild-Search-Drawer, Burger-Menue). Ersetzt die frueher zweifach kopierte
 * Inline-Logik aus sections/header.liquid und sections/search-drawer.liquid.
 *
 * Nutzt die geteilte Infrastruktur:
 *   - window.__tsCtx   (snippets/ts-context.liquid): Typesense-Zugang, isB2B, ...
 *   - window.TSCore    (assets/ts-search-core.js):   Preis-/HTML-Helfer
 * Ladereihenfolge daher IMMER: ts-context -> ts-search-core -> ts-autocomplete.
 *
 * Aufruf (idempotent, mehrfaches attach auf dasselbe Input = no-op):
 *   window.TSAutocomplete.attach({
 *     input:  '#selektor' | HTMLElement,   // Suchfeld
 *     panel:  '#selektor' | HTMLElement,   // Ergebnis-Container
 *     source: 'header' | 'search-drawer' | 'menu-drawer',
 *     boundary?: '#selektor' | HTMLElement, // Klick-innerhalb schliesst NICHT
 *     requireVisible?: bool,                // nur anhaengen wenn sichtbar (Header)
 *     maxResults?: number,                  // per_page (Default 8)
 *     showAllLink?: bool,                   // "Alle Ergebnisse"-Zeile (Default true)
 *     onResultsToggle?: fn(isOpen)          // Callback beim Ein-/Ausblenden
 *     history?: bool,                       // Suchverlauf "Zuletzt gesucht" (Default true)
 *     historyOnFocus?: bool                 // Verlauf schon beim Fokus zeigen (Default true);
 *                                           // false = erst beim Antippen/Klick ins Feld
 *   });
 *
 * Suchverlauf (BA-45, 04.10.2026): siehe Block "Suchverlauf" unten. Texte stehen
 * wie die uebrigen Autocomplete-Texte hart im Modul (keine locales-Anbindung).
 *
 * Damit die attach()-Aufrufe unabhaengig von der Skript-Reihenfolge funktionieren,
 * definieren die Sektionen einen winzigen Inline-Stub, der Konfigurationen in eine
 * Queue schiebt; dieses Modul (defer, laeuft nach dem HTML-Parsing) arbeitet die
 * Queue ab und ersetzt attach() durch die echte Implementierung.
 *
 * Defensive: fehlt window.__tsCtx oder window.TSCore, tut das Modul nichts
 * (Konsolen-Warnung, kein Throw) — die nativen GET-Formulare bleiben nutzbar.
 */
(function () {
  'use strict';

  var existing = window.TSAutocomplete;
  if (existing && existing.__real) return; // Doppel-Load: echtes Modul bereits aktiv
  var queued = (existing && existing._queue) ? existing._queue.slice() : [];

  function ctxOk() {
    if (!window.__tsCtx || !window.TSCore) {
      if (window.console && console.warn) {
        console.warn('[TSAutocomplete] window.__tsCtx oder window.TSCore fehlt — Sofortsuche inaktiv, native Suche bleibt nutzbar.');
      }
      return false;
    }
    return true;
  }

  function resolve(ref) {
    if (!ref) return null;
    if (typeof ref === 'string') return document.querySelector(ref);
    return ref;
  }

  function debounce(fn, ms) {
    var t;
    return function () {
      var args = arguments, self = this;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(self, args); }, ms);
    };
  }

  function esc(str) {
    return window.TSCore ? window.TSCore.escapeHTML(str || '') : String(str || '');
  }

  function priceHtml(price, compareAt) {
    if (!window.TSCore) return '';
    return window.TSCore.renderPriceHtml(price, compareAt, { compact: true });
  }

  function highlight(rawText, query) {
    var text = esc(rawText || '');
    if (!query) return text;
    var re = new RegExp('(' + String(query).replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + ')', 'gi');
    return text.replace(re, '<mark>$1</mark>');
  }

  // ---------------------------------------------------------------------------
  // Suchverlauf "Zuletzt gesucht" (BA-45, 04.10.2026)
  //
  // Datenschutz: Der Verlauf liegt NUR im Browser des Besuchers (localStorage,
  // Schluessel bs_suchverlauf). Gespeichert wird ausschliesslich der Suchbegriff,
  // keine Zeitstempel, keine Kennungen. Nichts davon geht an den Server, an
  // Typesense oder an denk-collect; keine Cookies. Ist der Speicher gesperrt
  // (privates Fenster, blockierte Website-Daten), gibt es still keinen Verlauf.
  //
  // Alle Funktionen nehmen optional ein Storage-Objekt (fuer Tests); ohne
  // Argument gilt window.localStorage. Jeder Zugriff steckt in try/catch.
  // ---------------------------------------------------------------------------
  var HIST_KEY = 'bs_suchverlauf';
  var HIST_MAX = 8;
  var HIST_MIN_LEN = 2;
  var HIST_MAX_LEN = 100;

  function histStorage(s) {
    if (s !== undefined) return s;
    try { return window.localStorage || null; } catch (e) { return null; }
  }

  // Getrimmt, Mehrfach-Leerraum zu einem Leerzeichen, auf 100 Zeichen gekappt.
  // Liefert '' fuer alles, was nicht gespeichert werden soll (< 2 Zeichen).
  function histNormalize(term) {
    if (typeof term !== 'string') return '';
    var t = term.replace(/\s+/g, ' ').trim().slice(0, HIST_MAX_LEN).trim();
    return t.length >= HIST_MIN_LEN ? t : '';
  }

  function histKey(term) { return term.toLowerCase(); }

  // Bereinigt: nur gueltige Strings, case-insensitiv dedupliziert, max. 8.
  function histSanitize(list) {
    var out = [], seen = {};
    if (!Array.isArray(list)) return out;
    for (var i = 0; i < list.length && out.length < HIST_MAX; i++) {
      var t = histNormalize(list[i]);
      if (!t) continue;
      var k = histKey(t);
      if (seen[k]) continue;
      seen[k] = true;
      out.push(t);
    }
    return out;
  }

  function histLoad(storage) {
    var s = histStorage(storage);
    if (!s) return [];
    try { return histSanitize(JSON.parse(s.getItem(HIST_KEY) || '[]')); } catch (e) { return []; }
  }

  function histSave(list, storage) {
    var s = histStorage(storage);
    if (!s) return;
    try {
      if (list.length) s.setItem(HIST_KEY, JSON.stringify(list));
      else s.removeItem(HIST_KEY);
    } catch (e) {}
  }

  // Neuester Begriff zuerst; vorhandener Eintrag (case-insensitiv) wandert nach oben.
  function histAdd(term, storage) {
    var t = histNormalize(term);
    var list = histLoad(storage);
    if (!t) return list;
    var k = histKey(t);
    list = [t].concat(list.filter(function (x) { return histKey(x) !== k; })).slice(0, HIST_MAX);
    histSave(list, storage);
    return list;
  }

  function histRemove(term, storage) {
    var k = histKey(String(term == null ? '' : term).replace(/\s+/g, ' ').trim());
    var list = histLoad(storage).filter(function (x) { return histKey(x) !== k; });
    histSave(list, storage);
    return list;
  }

  function histClear(storage) { histSave([], storage); return []; }

  // Verlauf nur bei leerem Feld und vorhandenen Eintraegen (kein leerer Block).
  function histShouldShow(value, entries) {
    return !String(value == null ? '' : value).trim() && Array.isArray(entries) && entries.length > 0;
  }

  var HIST_CLOCK = '<svg class="ts-ac-hist-icon" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true" focusable="false">'
    + '<circle cx="8" cy="8" r="6.25" stroke="currentColor" stroke-width="1.5"/>'
    + '<path d="M8 4.75V8l2.25 1.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  var HIST_X = '<svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true" focusable="false">'
    + '<path d="M2.5 2.5l7 7M9.5 2.5l-7 7" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>';

  function attrEsc(str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#039;');
  }

  // Block "Zuletzt gesucht": Zeilen als Links auf /search (gleiches Ziel wie
  // Enter im Suchformular: q + type=product), je Zeile ein Entfernen-Knopf.
  function histHtml(entries, listboxId, optIdFn) {
    var titleId = listboxId + '-verlauf';
    return '<div class="ts-ac-hist">'
      + '<p class="ts-ac-hist-title" id="' + titleId + '">Zuletzt gesucht</p>'
      + '<ul class="ts-ac-hist-list" role="listbox" id="' + listboxId + '" aria-labelledby="' + titleId + '">'
      + entries.map(function (term, i) {
        var t = attrEsc(term);
        return '<li class="ts-ac-hist-row" role="presentation">'
          + '<a href="/search?q=' + encodeURIComponent(term) + '&amp;type=product" class="ts-ac-hist-item" role="option"'
          + ' id="' + optIdFn(i) + '" aria-selected="false" data-term="' + t + '">'
          + HIST_CLOCK + '<span class="ts-ac-hist-term">' + t + '</span></a>'
          + '<button type="button" class="ts-ac-hist-remove" data-term="' + t + '" aria-label="Eintrag „' + t + '“ entfernen">' + HIST_X + '</button>'
          + '</li>';
      }).join('')
      + '</ul>'
      + '<button type="button" class="ts-ac-hist-clear">Verlauf löschen</button>'
      + '</div>';
  }

  var historyApi = {
    KEY: HIST_KEY,
    MAX: HIST_MAX,
    normalize: histNormalize,
    load: histLoad,
    add: histAdd,
    remove: histRemove,
    clear: histClear,
    shouldShow: histShouldShow,
    html: histHtml
  };

  // Query-Parameter identisch zu beiden frueheren Inline-Implementierungen.
  // Typesense-Suchparameter (rein, ohne Netzwerk; fuer Tests exportiert).
  // filter_by kommt aus TSCore.filterBy (ts-search-core.js), gleich fuer alle
  // Besucher. Seit dem Zwillings-Rueckbau 19.10.2026 ist die Klausel ohne
  // Basisfilter leer (frueher I103 `b2b_only:false`), dann entfaellt filter_by.
  function buildParams(query, maxResults) {
    var params = new URLSearchParams({
      q: query,
      query_by: 'title,sku,manufacturer_sku,brand,search_keywords',
      query_by_weights: '10,8,8,4,6',
      per_page: String(maxResults || 8),
      sort_by: '_text_match:desc,popularity_score:desc',
      num_typos: '2,0,0,2,2',
      split_join_tokens: 'always',
      // Teilstring-Suche auf SKU-Feldern (Index products_v7 hat infix:true auf sku+manufacturer_sku)
      infix: 'off,always,always,off,off'
    });
    var filterBy = window.TSCore && window.TSCore.filterBy ? window.TSCore.filterBy('') : '';
    if (filterBy) params.set('filter_by', filterBy);
    return params;
  }

  function doFetch(query, maxResults) {
    var cfg = (window.__tsCtx || {}).typesense || {};
    var params = buildParams(query, maxResults);
    return fetch('https://' + cfg.host + '/collections/' + (cfg.collection || 'products') + '/documents/search?' + params, {
      headers: { 'X-TYPESENSE-API-KEY': cfg.searchOnlyKey }
    }).then(function (res) { return res.json(); });
  }

  function attach(opts) {
    opts = opts || {};
    if (!ctxOk()) return;

    var input = resolve(opts.input);
    var panel = resolve(opts.panel);
    if (!input || !panel) {
      if (window.console && console.warn) {
        console.warn('[TSAutocomplete] input/panel nicht gefunden (source=' + (opts.source || '?') + ').');
      }
      return;
    }
    if (input.dataset.tsAcAttached) return;                 // idempotent
    if (opts.requireVisible && input.offsetParent === null) return; // z.B. Header-Suche nur ab 1150px sichtbar
    input.dataset.tsAcAttached = '1';

    var source = opts.source || 'search';
    var maxResults = opts.maxResults || 8;
    var showAllLink = opts.showAllLink !== false;
    var boundary = resolve(opts.boundary);
    var onToggle = typeof opts.onResultsToggle === 'function' ? opts.onResultsToggle : null;
    var historyEnabled = opts.history !== false;
    var historyOnFocus = opts.historyOnFocus !== false;

    // Eindeutige ARIA-IDs pro Instanz, damit mehrere Instanzen auf einer Seite
    // (Header + Drawer + Menue) sich nicht die aria-activedescendant-Verdrahtung
    // zerschiessen.
    var listboxId = 'ts-ac-listbox-' + source;
    function optId(i) { return 'ts-ac-opt-' + source + '-' + i; }

    input.setAttribute('role', 'combobox');
    input.setAttribute('aria-autocomplete', 'list');
    input.setAttribute('aria-expanded', 'false');
    input.setAttribute('aria-controls', listboxId);

    var activeIndex = -1;
    // Was das Panel gerade zeigt: 'history' (Zuletzt gesucht), 'results'
    // (Laden/Treffer/Leer/Fehler) oder null (geschlossen).
    var mode = null;

    function fireToggle(open) {
      if (onToggle) { try { onToggle(open); } catch (e) {} }
    }

    function show(html) {
      panel.innerHTML = html;
      panel.hidden = false;
    }

    function setLoading() {
      // Spinner im Ergebnis-Container; fireToggle erst bei echten Ergebnissen
      // (1:1 zum alten Drawer: Featured-Links/Menue bleiben waehrend des Ladens).
      show('<div class="ts-autocomplete-loading"><span class="ts-spinner"></span></div>');
      mode = 'results';
    }

    // Verlauf zeigen, wenn das Feld leer ist und Eintraege existieren. Ersetzt den
    // Vorschlagsbereich im selben Panel (kein zusaetzlicher Container, kein Request).
    function showHistory() {
      if (!historyEnabled) return false;
      var entries = histLoad();
      if (!histShouldShow(input.value, entries)) return false;
      activeIndex = -1;
      show(histHtml(entries, listboxId, optId));
      mode = 'history';
      input.setAttribute('aria-expanded', 'true');
      input.removeAttribute('aria-activedescendant');
      fireToggle(true);
      return true;
    }

    function showHistoryOrClear() {
      if (!showHistory()) clearResults();
    }

    function buildItem(p, query, i) {
      var imgSrc = esc(p.image_url || '');
      var title = highlight(p.title || '', query);
      var brand = p.brand ? '<span class="ts-autocomplete-brand">' + esc(p.brand) + '</span>' : '';
      var sku = p.sku ? '<span class="ts-sku">' + esc(p.sku) + '</span>' : '';
      var pHtml = p.price != null ? priceHtml(p.price, p.compare_at_price || null) : '';
      var avail = p.availability || (p.in_stock ? 'sofort' : 'ausverkauft');
      var availColor = avail === 'sofort' ? 'green' : avail === 'bestellbar' ? 'yellow' : 'red';
      var availLabel = avail === 'sofort' ? 'Auf Lager' : avail === 'bestellbar' ? 'Bestellbar' : 'Ausverkauft';
      // Farb-Punkt (dekorativ) + sichtbares Label wie auf der Karte.
      var availDot = '<span class="ts-ac-avail"><span class="ts-dot ts-dot-' + availColor + '" aria-hidden="true"></span>' + availLabel + '</span>';
      return '<a href="/products/' + esc(p.handle || '') + '" class="ts-autocomplete-item" role="option" id="' + optId(i) + '" aria-selected="false">'
        + '<div class="ts-autocomplete-img-wrap">'
        + (imgSrc ? '<img src="' + imgSrc + '" alt="" width="56" height="56" loading="lazy" />' : '')
        + '</div>'
        + '<div class="ts-autocomplete-info">' + brand + '<strong class="ts-autocomplete-title">' + title + '</strong>' + sku + '</div>'
        + '<div class="ts-autocomplete-price">' + pHtml + availDot + '</div>'
        + '</a>';
    }

    function allBtn(q, total) {
      if (!showAllLink) return '';
      return '<a href="/search?q=' + q + '&type=product" class="ts-autocomplete-all-btn">Alle ' + total + ' Ergebnisse anzeigen →</a>';
    }

    function renderResults(data, query) {
      activeIndex = -1;
      mode = 'results';
      var hits = data.hits || [];
      var total = data.found || 0;
      var q = encodeURIComponent(input.value || query);

      if (!hits.length) {
        show('<div class="ts-autocomplete-empty">'
          + '<p>Keine Treffer für <strong>' + esc(query) + '</strong></p>'
          + '<a href="/search?q=' + q + '&type=product" class="ts-autocomplete-all-btn">Trotzdem alle Ergebnisse durchsuchen →</a>'
          + '</div>');
        input.setAttribute('aria-expanded', 'true');
        input.removeAttribute('aria-activedescendant');
        fireToggle(true);
        return;
      }

      show(
        '<div class="ts-autocomplete-header"><span class="ts-autocomplete-count">' + total + ' Treffer</span></div>'
        + '<div class="ts-autocomplete-list" role="listbox" id="' + listboxId + '" aria-label="Suchvorschläge">'
        + hits.map(function (h, i) { return buildItem(h.document, query, i); }).join('') + '</div>'
        + allBtn(q, total));
      input.setAttribute('aria-expanded', 'true');
      input.removeAttribute('aria-activedescendant');
      fireToggle(true);
    }

    function renderError(val) {
      mode = 'results';
      // Fetch-Fehler: keine Sackgasse, sondern Link zur nativen Suche.
      show('<div class="ts-autocomplete-empty">'
        + '<p>Die Sofortsuche ist gerade nicht verfügbar.</p>'
        + '<a href="/search?q=' + encodeURIComponent(val) + '&type=product" class="ts-autocomplete-all-btn">Zur Suche →</a>'
        + '</div>');
      input.setAttribute('aria-expanded', 'true');
      fireToggle(true);
    }

    function clearResults() {
      panel.hidden = true;
      panel.innerHTML = '';
      activeIndex = -1;
      mode = null;
      input.setAttribute('aria-expanded', 'false');
      input.removeAttribute('aria-activedescendant');
      fireToggle(false);
    }

    // Sequenz-Guard: eine langsame alte Response darf eine neuere nicht ueberschreiben
    var fetchSeq = 0;
    var debounced = debounce(function (val) {
      // Feld leer (geloescht): Verlauf statt Vorschlaegen, sonst schliessen.
      if (!val.trim()) { fetchSeq++; showHistoryOrClear(); return; }
      setLoading();
      var mySeq = ++fetchSeq;
      doFetch(val, maxResults).then(function (data) {
        if (mySeq !== fetchSeq) return;
        renderResults(data, val);
      }).catch(function () {
        if (mySeq !== fetchSeq) return;
        renderError(val);
      });
    }, 200);

    input.addEventListener('input', function () {
      // Sobald getippt wird, weicht der Verlauf sofort (Spinner im selben
      // Panel, kein Aufblitzen der Featured-Links waehrend des Debounce).
      if (mode === 'history' && input.value.trim()) setLoading();
      debounced(input.value);
    });

    // Verlauf beim Fokus bzw. Antippen eines leeren Feldes. historyOnFocus:false
    // (Burger-Menue): dort setzt das Theme beim Oeffnen auf Desktop den Fokus
    // programmatisch ins Feld; der Verlauf soll erst auf Klick/Tap erscheinen.
    if (historyEnabled) {
      if (historyOnFocus) {
        input.addEventListener('focus', function () { if (panel.hidden) showHistory(); });
      }
      input.addEventListener('click', function () { if (panel.hidden) showHistory(); });
    }

    // type=reset (z.B. Clear-Button im Search-Drawer) feuert kein input-Event
    if (input.form) {
      input.form.addEventListener('reset', function () {
        fetchSeq++;
        setTimeout(showHistoryOrClear, 0);
      });
      // (a) Enter / Formular-Absenden: Begriff in den Verlauf.
      input.form.addEventListener('submit', function () { histAdd(input.value); });
    }

    input.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') { clearResults(); return; }
      if (panel.hidden) return;
      var items = Array.prototype.slice.call(panel.querySelectorAll('.ts-autocomplete-item, .ts-ac-hist-item'));
      if (!items.length) return;
      function syncActive() {
        items.forEach(function (el, i) {
          var on = i === activeIndex;
          el.classList.toggle('is-active', on);
          el.setAttribute('aria-selected', on ? 'true' : 'false');
        });
        var cur = items[activeIndex];
        if (cur && cur.id) input.setAttribute('aria-activedescendant', cur.id);
        else input.removeAttribute('aria-activedescendant');
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        activeIndex = Math.min(activeIndex + 1, items.length - 1);
        syncActive();
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        activeIndex = Math.max(activeIndex - 1, 0);
        syncActive();
      } else if (e.key === 'Enter' && activeIndex >= 0) {
        // Nur bei aktiver Auswahl abfangen; sonst faellt Enter durch ->
        // natives Form-Submit auf /search bleibt erhalten.
        e.preventDefault();
        items[activeIndex].click();
      }
    });

    // denk-collect: search_result_click aus dem Dropdown — misst WELCHES Ergebnis
    // auf WELCHER Position (1-basiert) geklickt wird. Vorher NUR im Search-Drawer
    // verdrahtet (Datenluecke); jetzt fuer ALLE Instanzen, mit source-Feld.
    // Feuert nur wenn denk geladen ist; PII-Guard greift serverseitig.
    panel.addEventListener('click', function (ev) {
      try {
        var link = ev.target && ev.target.closest ? ev.target.closest('a.ts-autocomplete-item') : null;
        if (!link || !panel.contains(link)) return;
        if (!window.denk || typeof window.denk.track !== 'function') return;
        var items = panel.querySelectorAll('a.ts-autocomplete-item');
        var idx = Array.prototype.indexOf.call(items, link);
        if (idx < 0) return;
        var q = (input.value || '').trim();
        var props = { pos: idx + 1, target: link.getAttribute('href') || '', path: location.pathname, source: source };
        if (q) props.search_term = q.slice(0, 100);
        window.denk.track('search_result_click', props);
      } catch (e) {}
    });

    // Suchverlauf: Klicks im Panel. (b) "Alle Ergebnisse" und (c) Vorschlag
    // speichern den EINGETIPPTEN Begriff (nicht den Produkttitel).
    panel.addEventListener('click', function (ev) {
      var t = ev.target;
      if (!t || !t.closest) return;
      var kb = ev.detail === 0; // per Tastatur ausgeloest -> Fokus zurueck ins Feld
      var rm = t.closest('.ts-ac-hist-remove');
      if (rm) {
        ev.preventDefault();
        histRemove(rm.getAttribute('data-term'));
        showHistoryOrClear();
        if (kb) input.focus();
        return;
      }
      if (t.closest('.ts-ac-hist-clear')) {
        ev.preventDefault();
        histClear();
        clearResults();
        if (kb) input.focus();
        return;
      }
      var hi = t.closest('.ts-ac-hist-item');
      if (hi) {
        // Begriff ins Feld + Suche ueber den Link (/search?q=...&type=product,
        // dasselbe Ziel wie Enter); der Begriff rueckt im Verlauf nach oben.
        var term = hi.getAttribute('data-term') || '';
        input.value = term;
        histAdd(term);
        return;
      }
      if (t.closest('a.ts-autocomplete-item, .ts-autocomplete-all-btn')) histAdd(input.value);
    });

    // Klick ausserhalb schliesst. boundary (z.B. Header-Suchzeile, Search-Drawer,
    // Suchformular im Menue) zaehlt als "innerhalb".
    document.addEventListener('click', function (e) {
      if (panel.hidden) return;
      var t = e.target;
      // Ziel wurde gerade aus dem Panel entfernt (Verlauf-Eintrag geloescht und
      // neu gerendert) -> zaehlt als Klick innerhalb, nicht schliessen.
      if (t && t.isConnected === false) return;
      if (panel.contains(t)) return;
      if (t === input || input.contains(t)) return;
      if (boundary && boundary.contains(t)) return;
      clearResults();
    });
  }

  var real = { attach: attach, buildParams: buildParams, history: historyApi, __real: true };
  window.TSAutocomplete = real;
  // Vor dem Modul-Load per Inline-Stub eingereihte Konfigurationen abarbeiten.
  queued.forEach(function (o) { try { attach(o); } catch (e) {} });
})();
